import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Heart, Pause, Play, Share2, Headphones, Trophy } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { coverSrc } from "../lib/api";
import { LiveNumber } from "./LiveNumber";

export const Cover = ({ mix, className = "" }) => {
  const src = coverSrc(mix.cover_url);
  return src ? (
    <img src={src} alt={mix.title} className={`w-full h-full object-cover ${className}`} loading="lazy" />
  ) : (
    <div className={`w-full h-full grid place-items-center bg-[#0A1424] font-display text-5xl text-[#1FD1FF]/60 ${className}`}>T-REX</div>
  );
};

export const MixActions = ({ mix, idx }) => {
  const { downloadMix, likeMix, setShareMix } = usePlayer();
  return (
    <div className="flex items-center gap-1">
      <button data-testid={`mix-card-like-button-${idx}`} onClick={() => likeMix(mix)} className="flex items-center gap-1.5 px-2.5 py-2 font-mono text-xs hover:text-[#1FD1FF] transition-colors" aria-label="Like">
        <motion.span whileTap={{ scale: 1.5 }}><Heart className={`w-4 h-4 ${mix.liked ? "fill-[#1FD1FF] text-[#1FD1FF]" : ""}`} /></motion.span>
        <span data-testid={`mix-card-likes-${idx}`}><LiveNumber value={mix.likes} /></span>
      </button>
      <button data-testid={`mix-card-share-button-${idx}`} onClick={() => setShareMix(mix)} className="px-2.5 py-2 hover:text-[#1FD1FF] transition-colors" aria-label="Share"><Share2 className="w-4 h-4" /></button>
      <button data-testid={`mix-card-download-button-${idx}`} onClick={() => downloadMix(mix)} className="flex items-center gap-1.5 px-2.5 py-2 font-mono text-xs hover:text-[#1FD1FF] transition-colors" aria-label="Download">
        <Download className="w-4 h-4" /><span data-testid={`mix-card-downloads-${idx}`}><LiveNumber value={mix.downloads} /></span>
      </button>
    </div>
  );
};

export const MixCard = ({ mix, idx, list }) => {
  const { playMix, mix: current, playing } = usePlayer();
  const active = current?.id === mix.id && playing;
  return (
    <motion.article
      layout
      data-testid={`mix-card-${idx}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: (idx % 3) * 0.08 }}
      className="group relative"
    >
      <div className="relative aspect-square overflow-hidden [clip-path:polygon(0_0,100%_0,100%_90%,90%_100%,0_100%)] bg-[#0A1424]">
        <Cover mix={mix} className="transition-transform duration-[1.2s] ease-out group-hover:scale-110 grayscale-[40%] group-hover:grayscale-0" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#04070D] via-transparent to-transparent opacity-90" />
        <span className="absolute top-3 left-3 font-mono text-[11px] uppercase tracking-widest bg-black/70 backdrop-blur px-2 py-1">{mix.genre}</span>
        {mix.plays >= 1000 && (
          <span data-testid={`mix-card-milestone-${idx}`} className="absolute top-11 left-3 flex items-center gap-1 font-mono text-[11px] font-bold bg-white text-black px-2 py-1">
            <Trophy className="w-3 h-3" /> {[10000, 5000, 1000].find((v) => mix.plays >= v) / 1000}K
          </span>
        )}
        <span className="absolute top-3 right-3 font-mono text-[11px] bg-[#1FD1FF] text-black px-2 py-1">{mix.bpm || "—"} BPM{mix.key ? ` · ${mix.key}` : ""}</span>
        <button
          data-testid={`mix-card-play-button-${idx}`}
          onClick={() => playMix(mix, list)}
          className="absolute bottom-5 left-5 w-16 h-16 rounded-full bg-[#1FD1FF] text-black grid place-items-center shadow-[0_0_30px_rgba(31,209,255,0.4)] hover:scale-110 transition-transform"
          aria-label={active ? "Pause" : "Play"}
        >
          {active ? <Pause className="w-6 h-6 fill-current" /> : <Play className="w-6 h-6 fill-current ml-1" />}
        </button>
        <span className="absolute bottom-6 right-5 font-mono text-xs flex items-center gap-1.5 text-white" data-testid={`mix-card-plays-${idx}`}>
          <Headphones className="w-3.5 h-3.5" /> <LiveNumber value={mix.plays} />
        </span>
      </div>
      <div className="pt-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/mix/${mix.id}`} data-testid={`mix-card-title-${idx}`} className="font-display text-3xl leading-none hover:text-[#1FD1FF] transition-colors block truncate">{mix.title}</Link>
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#A8B3C4] mt-1.5 truncate">{(mix.tags || []).map((t) => `#${t}`).join("  ")}</p>
        </div>
        <span className="font-mono text-xs text-[#A8B3C4] pt-1">{mix.duration}</span>
      </div>
      <div className="mt-2 -ml-2.5 text-[#A8B3C4]"><MixActions mix={mix} idx={idx} /></div>
    </motion.article>
  );
};
